"use client";
import React, { useCallback, useState } from "react";
import styles from "./calculator.module.scss";
import { AddCustomItem, Card, ItemCard } from "./client";
import classNames from "classnames";
import Image from "next/image";
import { FiArrowLeft, FiSearch } from "react-icons/fi";
import Link from "next/link";
import CloseIcon from "component/closeIcon";
import logo from "assets/images/logo.png";
import logoFull from "assets/images/logo-full.png";
import header from "component/header.module.css";
import { useRouter } from "next/navigation";
import useSWR from "swr";
import axios from "axios";
import { basicFetcher } from "utils/fetcher";

function CalculatorPage(props: { onClose?: (open: boolean) => void }) {
  const { onClose } = props;
  const [step, setStep] = useState(0);
  const [search, setSearch] = useState("");
  const [pickedList, setPickedList] = useState<Record<string, PickedItem>>({});
  const [result, setResult] = useState<EnergyCalcResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calcError, setCalcError] = useState<string | null>(null);
  const router = useRouter();

  const { data: items = [], isLoading: itemsLoading } = useSWR<CalculatorItem[]>(
    step === 1
      ? `calculator-items/${search ? `?search=${encodeURIComponent(search)}` : ""}`
      : null,
    basicFetcher
  );

  const handleClose = () => {
    if (onClose) {
      onClose(false);
    } else {
      router.back();
    }
  };

  const handleQuantityChange = useCallback((item: CalculatorItem, quantity: number) => {
    if (quantity < 0) return;
    const key = String(item.id);
    setPickedList((prev) => {
      const next = { ...prev };
      if (quantity === 0) {
        delete next[key];
      } else if (next[key]) {
        next[key] = { ...next[key], quantity };
      } else {
        next[key] = {
          id: key,
          name: item.name,
          power: item.power,
          quantity,
          hours: item.avg_hr_per_day,
          calculator_item_id: item.id,
        };
      }
      return next;
    });
  }, []);

  const updateHours = useCallback((id: string, hours: number) => {
    if (hours < 0.0083 || hours > 24) return;
    setPickedList((prev) => {
      if (!prev[id]) return prev;
      return { ...prev, [id]: { ...prev[id], hours } };
    });
  }, []);

  const updatePower = useCallback((id: string, power: number) => {
    if (power <= 0) return;
    setPickedList((prev) => {
      if (!prev[id]) return prev;
      return { ...prev, [id]: { ...prev[id], power } };
    });
  }, []);

  const removeItem = useCallback((id: string) => {
    setPickedList((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const addToPicked = useCallback((item: PickedItem) => {
    setPickedList((prev) => ({ [item.id]: item, ...prev }));
  }, []);

  const handleCalculate = async () => {
    setIsCalculating(true);
    setCalcError(null);
    try {
      const payload = {
        appliances: Object.values(pickedList).map((item) =>
          item.calculator_item_id !== undefined
            ? { calculator_item_id: item.calculator_item_id, quantity: item.quantity, hours: item.hours }
            : { name: item.name, power: item.power, quantity: item.quantity, hours: item.hours }
        ),
      };
      const { data } = await axios.post<EnergyCalcResult>("energy-calculator/", payload);
      setResult(data);
      setStep(3);
    } catch {
      setCalcError("Something went wrong. Please try again.");
    } finally {
      setIsCalculating(false);
    }
  };

  if (step === 0) {
    return (
      <div>
        <Header text="Energy needs calculator" onClose={handleClose} />
        <div className={styles.step0}>
          <div className="container mx-auto text-center">
            <h1 className="text-center mx-auto">Let's Calculate your energy Needs</h1>
            <p className="mx-auto">
              Do you need help with the ideal energy-efficient product for your needs? Simply highlight how many
              appliances or devices you typically use, and let our Energy Needs Calculator help you narrow your options
              to those that best match your consumption patterns.
            </p>
            <p>
              Please note that this Energy Needs Calculator is solely for informational purposes. The provided results
              are based on your inputs and are not intended to replace professional advice. If you have any doubts
              regarding the results, kindly send an email to speak to an expert.
            </p>
            <button
              onClick={() => setStep(1)}
              style={{ width: 158, maxWidth: "100%" }}
              className="btn btn-lg btn-green">
              Start
            </button>
          </div>
          <div className="container"></div>
        </div>
      </div>
    );
  }
  return (
    <>
      {step === 1 ? (
        <Header text="Select Items you want to Power" onClose={handleClose} />
      ) : step === 2 ? (
        <Header text="Edit the wattage and hourly usage" onClose={handleClose} />
      ) : (
        <Header text="View Recommended Solutions" onClose={handleClose} />
      )}
      <div className="container" style={{ marginBottom: 81 }}>
        {step === 1 && (
          <div className={styles.top}>
            <h2>Let's start with selecting the items you want to power</h2>
            <div className="">
              <div className="">
                <div className="btn btn-white" style={{ paddingBlock: 0, paddingRight: 0 }}>
                  <FiSearch />
                  <input
                    value={search}
                    type="search"
                    placeholder="Search appliances..."
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="">
                <AddCustomItem addToPicked={addToPicked} />
                <button form="s1" disabled={!Object.keys(pickedList).length} className="btn btn-green">
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
        {step === 2 && (
          <div className={styles.top}>
            <h2>Edit the wattage and hourly usage for each item</h2>
            <div className="">
              <button type="button" onClick={() => setStep(1)}>
                <FiArrowLeft /> Back
              </button>
              <button form="s2" disabled={!Object.keys(pickedList).length || isCalculating} className="btn btn-green">
                {isCalculating ? "Calculating..." : "See Results"}
              </button>
            </div>
            {calcError && <p style={{ color: "red", marginTop: "0.5rem" }}>{calcError}</p>}
          </div>
        )}
        {step === 3 && result && (
          <div style={{ textAlign: "center", marginTop: "2rem" }}>
            <button
              className="btn btn-black"
              type="button"
              style={{ marginBottom: "1rem" }}
              onClick={() => setStep(1)}>
              <FiArrowLeft /> Back
            </button>
          </div>
        )}
        <form
          id="s1"
          hidden={step !== 1}
          onSubmit={(e) => {
            e.preventDefault();
            setStep(2);
          }}>
          {itemsLoading ? (
            <ul className={styles.appcardlist}>
              {Array.from({ length: 12 }).map((_, i) => (
                <li key={i} className="pulse" style={{ height: 80 }} />
              ))}
            </ul>
          ) : (
            <ul className={styles.appcardlist}>
              {items.map((item) => (
                <ItemCard
                  key={item.id}
                  appliance={item}
                  quantity={pickedList[String(item.id)]?.quantity ?? 0}
                  onQuantityChange={(qty) => handleQuantityChange(item, qty)}
                />
              ))}
            </ul>
          )}
          <div style={{ textAlign: "center", marginBlock: "2rem" }}>
            <p>Can't find what you are looking for? You can add a custom item</p>
            <br />
            <AddCustomItem addToPicked={addToPicked} />
          </div>
          <button
            style={{ width: 298, maxWidth: "100%" }}
            disabled={!Object.keys(pickedList).length}
            className="btn btn-green">
            Continue
          </button>
          <hr style={{ marginBlock: "1rem" }} />
          <small>
            This energy needs calculator is offered for informational purposes only. The results it provides are based
            on your inputs, and are not intended to substitute for professional advice. Please consult an expert if you
            aren't sure about your results.
          </small>
        </form>

        <form
          id="s2"
          hidden={step !== 2}
          onSubmit={(e) => {
            e.preventDefault();
            handleCalculate();
          }}>
          <Step2
            appliances={pickedList}
            updateHours={updateHours}
            updatePower={updatePower}
            removeItem={removeItem}
          />
          <button
            style={{ width: 298, maxWidth: "100%" }}
            disabled={!Object.keys(pickedList).length || isCalculating}
            className="btn btn-green">
            {isCalculating ? "Calculating..." : "See Results"}
          </button>
          <hr style={{ marginBlock: "1rem" }} />
          <small>
            This energy needs calculator is offered for informational purposes only. The results it provides are based
            on your inputs, and are not intended to substitute for professional advice. Please consult an expert if you
            aren't sure about your results.
          </small>
        </form>
      </div>
      {step === 3 && result && (
        <>
          <div style={{ backgroundColor: "var(--white)", paddingBlock: "3rem" }}>
            <div className="container">
              <p style={{ textAlign: "center", marginBottom: "1.5rem" }}>{result.match_message}</p>
              <div className={styles.summaryGrid}>
                <SummaryCard label="Total Load" value={`${result.total_load_w} W`} />
                <SummaryCard label="Daily Consumption" value={`${result.total_daily_consumption_wh} Wh`} />
                <SummaryCard label="Recommended Inverter" value={`${result.recommended_inverter_kva} kVA`} />
                <SummaryCard label="Recommended Battery" value={`${result.recommended_battery_kwh} kWh`} />
                <SummaryCard label="Recommended Solar" value={`${result.recommended_solar_kw} kW`} />
                <SummaryCard label="Estimated Backup" value={`${result.estimated_backup_hours} hrs`} />
              </div>
            </div>
          </div>
          <div className={classNames("container", styles.table)}>
            <table>
              <thead>
                <tr>
                  <th>Appliance</th>
                  <th>Power</th>
                  <th>Hours</th>
                  <th>Quantity</th>
                  <th>Daily Usage</th>
                </tr>
              </thead>
              <tbody>
                {result.appliances.map((appliance, i) => (
                  <tr key={i}>
                    <td>{appliance.name}</td>
                    <td>{appliance.power}W</td>
                    <td>{appliance.hours}hrs</td>
                    <td>{appliance.quantity}</td>
                    <td>{appliance.daily_consumption}Wh</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th style={{ textAlign: "left" }}>
                    <b>Total</b>
                  </th>
                  <td colSpan={3}>
                    <b>{result.total_load_w} W</b>
                  </td>
                  <td>
                    <b>{result.total_daily_consumption_wh} Wh</b>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="container" style={{ textAlign: "center" }}>
            <h2 className={styles.title}>See your offers</h2>
            <p>Based on your energy usage, these are the products we recommend</p>
            <br />
            <small>Note: Please consult an expert if you aren't sure about your results.</small>
            <ul className="products">
              {result.recommendations.map((product) => (
                <li key={product.id}>
                  <Link onClick={() => onClose?.(false)} href={`/product/${product.id}`}>
                    <div className="productImage">
                      <img src={product.primary_img_url} alt={product.name} />
                    </div>
                    <h3>{product.name}</h3>
                    <p>₦{product.price.toLocaleString()}</p>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <hr />
        </>
      )}
    </>
  );
}

export default CalculatorPage;

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.summaryCard}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Step2({
  appliances,
  updateHours,
  updatePower,
  removeItem,
}: {
  appliances: Record<string, PickedItem>;
  updateHours: Updatr;
  updatePower: Updatr;
  removeItem: (id: string) => void;
}) {
  return (
    <ul className={styles.cards}>
      {Object.values(appliances).map((device) => (
        <Card
          key={device.id}
          props={device}
          updateHours={updateHours}
          updatePower={updatePower}
          removeFromList={() => removeItem(device.id)}
        />
      ))}
    </ul>
  );
}

function Header({ text, onClose }: { text: string; onClose: () => void }) {
  return (
    <header className={header.header}>
      <div className={classNames(header.nav, "container")}>
        <Link href="/">
          <Image width={30} height={30} src={logo} alt="logo" />
          <Image height={30} src={logoFull} alt="logo full" />
        </Link>
        <button
          className={header.need}
          style={{ fontWeight: 500, fontSize: "1.1875rem", textTransform: "capitalize" }}
          onClick={onClose}>
          {text}
        </button>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
          <button className="btn" onClick={onClose}>
            <CloseIcon />
            Close
          </button>
        </div>
      </div>
    </header>
  );
}
